import { api } from '@/config/api';

export type TemplateStatus = 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAUSED' | 'DISABLED';
export type TemplateCategory = 'marketing' | 'utility' | 'authentication';
export type TemplateType = 'STANDARD' | 'CAROUSEL';
export type HeaderType = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';
export type CardHeaderType = 'IMAGE' | 'VIDEO';
export type ButtonType = 'URL' | 'PHONE' | 'QUICK_REPLY' | 'COPY_CODE';

export interface TemplateButton {
  type: ButtonType;
  text: string;
  url?: string;
  phone?: string;
  countryCode?: string;        // ISO-2 like 'AE' or '+971' — UI only, helps render the picker
  payload?: string;            // QUICK_REPLY: optional payload to send instead of text
}

export interface TemplateLocation {
  latitude?: number;
  longitude?: number;
  name?: string;
  address?: string;
}

export interface TemplateCard {
  headerType?: CardHeaderType;
  headerImageUrl?: string;
  headerVideoUrl?: string;
  body: string;
  buttons?: TemplateButton[];
}

export interface Template {
  _id: string; templateUid: string; companyUid: string;
  /** WhatsApp account this template was submitted under. */
  accountUid?: string;
  parameterFormat?: 'POSITIONAL' | 'NAMED';
  name: string; language: string; category: TemplateCategory; type: TemplateType;
  headerType?: HeaderType;
  headerText?: string;
  headerImageUrl?: string;
  headerVideoUrl?: string;
  headerDocumentUrl?: string;
  headerDocumentFilename?: string;
  headerLocation?: TemplateLocation;
  body: string; footer?: string; buttons: TemplateButton[];
  cards?: TemplateCard[];
  bodyMeta: string; variableMap: Record<string, string>; headerVariableMap?: Record<string, string>;
  previewSamples: Record<string, string>;
  /** Use-case tags. 'chatbot' marks templates intended for bot flow use. */
  useCases?: string[];
  waTemplateId?: string; status: TemplateStatus; rejectionReason?: string;
  qualityScore?: string;
  submittedAt?: string; approvedAt?: string; rejectedAt?: string;
  aiGenerated: boolean; aiPrompt?: string; aiProvider?: string; aiModel?: string;
  createdBy: string; updatedBy?: string; createdAt: string; updatedAt: string;
}

export interface ListResponse { items: Template[]; total: number; page: number; limit: number; }

export interface CreateTemplatePayload {
  accountUid?: string;
  name: string; language: string; category: TemplateCategory;
  type?: TemplateType;
  headerType?: HeaderType;
  headerText?: string;
  headerImageUrl?: string;
  headerVideoUrl?: string;
  headerDocumentUrl?: string;
  headerDocumentFilename?: string;
  headerLocation?: TemplateLocation;
  body: string; footer?: string;
  buttons?: TemplateButton[];
  cards?: TemplateCard[];
  previewSamples?: Record<string, string>;
  /** Tag with one or more use cases — e.g. ['chatbot']. */
  useCases?: string[];
}

export type AiProviderName = 'openai' | 'claude' | 'qwen' | 'gemini' | 'mistral';

export interface AiAssistRequest {
  brief: string;
  category?: TemplateCategory;
  tone?: 'formal' | 'friendly' | 'promotional' | 'transactional';
  language?: string;
  /** When 'CAROUSEL' the AI returns cards[] instead of header/footer/buttons. */
  type?: TemplateType;
  provider?: AiProviderName;
  model?: string;
}

export interface AiAssistResponse {
  type?: TemplateType;
  category: TemplateCategory;
  headerType: HeaderType;
  headerText?: string;
  body: string;
  footer?: string;
  buttons: TemplateButton[];
  cards?: TemplateCard[];
  suggestedSampleValues?: Record<string, string>;
  reasoning?: string;
  creditsCharged?: number;
}

export interface SyncFromMetaResult { created: number; updated: number; skipped: number; total: number; }

export interface TestSendResult { ok: boolean; waMessageId?: string; error?: string; }

export const templatesService = {
  variables: async (): Promise<string[]> => (await api.get('/slighter_crm/templates/variables')).data,
  list: async (q: { q?: string; status?: string; category?: string; language?: string; accountUid?: string; useCase?: string; page?: number; limit?: number } = {}): Promise<ListResponse> =>
    (await api.get('/slighter_crm/templates', { params: q })).data,
  get: async (uid: string): Promise<Template> => (await api.get(`/slighter_crm/templates/${uid}`)).data,
  create: async (p: CreateTemplatePayload): Promise<Template> => (await api.post('/slighter_crm/templates', p)).data,
  update: async (uid: string, p: Partial<CreateTemplatePayload>): Promise<Template> => (await api.patch(`/slighter_crm/templates/${uid}`, p)).data,
  remove: async (uid: string) => { await api.delete(`/slighter_crm/templates/${uid}`); },
  submit: async (uid: string): Promise<Template> => (await api.post(`/slighter_crm/templates/${uid}/submit`)).data,
  events: async (uid: string) => (await api.get(`/slighter_crm/templates/${uid}/events`)).data,
  aiAssist: async (req: AiAssistRequest): Promise<AiAssistResponse> => (await api.post('/slighter_crm/templates/ai-assist', req)).data,
  syncFromMeta: async (accountUid?: string): Promise<SyncFromMetaResult> => (await api.post('/slighter_crm/templates/sync-from-meta', null, { params: { accountUid } })).data,
  syncOneFromMeta: async (uid: string): Promise<Template> =>
    (await api.post(`/slighter_crm/templates/${uid}/sync-from-meta`)).data,
  testSend: async (
    uid: string,
    body: {
      phone: string;
      variables?: Record<string, string>;
      headerOverride?: HeaderOverride;
    },
  ): Promise<TestSendResult> =>
    (await api.post(`/slighter_crm/templates/${uid}/test-send`, body)).data,
  /** Async delivery status stamped by the Meta status webhook. */
  sendStatus: async (waMessageId: string): Promise<{ deliveryStatus: string | null; failureReason: string | null }> =>
    (await api.get('/slighter_crm/templates/send-status', { params: { waMessageId } })).data,
  /** Agent reply from /chats — sends an APPROVED template to an existing lead. */
  sendToLead: async (
    uid: string,
    body: {
      leadUid: string;
      variables?: Record<string, string>;
      headerOverride?: HeaderOverride;
    },
  ): Promise<TestSendResult> =>
    (await api.post(`/slighter_crm/templates/${uid}/send-to-lead`, body)).data,
  /**
   * Start a NEW conversation from /chats. Meta's 24h rule means the first
   * message to someone who has never written to us must be an approved
   * template; this also creates the lead so the thread appears at once.
   */
  startConversation: async (
    uid: string,
    body: { phone: string; variables?: Record<string, string> },
  ): Promise<TestSendResult> =>
    (await api.post(`/slighter_crm/templates/${uid}/start-conversation`, body)).data,
};

export interface HeaderOverride {
  imageUrl?: string;
  videoUrl?: string;
  documentUrl?: string;
  documentFilename?: string;
  location?: TemplateLocation;
}
